import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { StarIcon } from "./icons";
import { AppPreviewCardProps } from "./types";

/** How many thumbnails fit the compact strip before it starts scrolling. */
const MAX_SCREENSHOTS = 4;

/** 1_234_567 → "1.2M" — the card has no room for grouped digits. */
const formatCount = (value: number): string => {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions >= 10 ? Math.round(millions) : millions.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}K`;
  }
  return String(value);
};

/**
 * Compact app preview — what an app icon shows on hover: icon, title, subtitle,
 * rating, a short meta line, a screenshot strip and a clamped description.
 *
 * Pure presentational, same content as the App Profile modal squeezed into a
 * tooltip-sized surface. The host decides where it hangs and what it fetches;
 * with `loading` the header still renders (the list already knows the icon and
 * the title) while the store-listing parts show placeholders.
 */
export const AppPreviewCard: React.FC<AppPreviewCardProps> = ({
  icon,
  title,
  subtitle,
  developer,
  rank,
  score,
  reviewsCount,
  category,
  price,
  contentRating,
  screenshots = [],
  description,
  descriptionClampLines = 3,
  loading = false,
  footnote,
  className,
}) => {
  const caption = subtitle || developer;
  const shots = screenshots.slice(0, MAX_SCREENSHOTS);

  // Dot-separated line; every part is optional, so build it, then join.
  const meta = [
    category,
    price,
    contentRating,
    reviewsCount ? `${formatCount(reviewsCount)} ratings` : null,
  ].filter(Boolean) as string[];

  return (
    <article className={cs(styles.card, className)}>
      <header className={styles.head}>
        {icon ? (
          <img className={styles.icon} src={icon} alt="" loading="lazy" />
        ) : (
          <span className={cs(styles.icon, styles.iconFallback)}>
            {title.slice(0, 1).toUpperCase()}
          </span>
        )}

        <div className={styles.headText}>
          <h4 className={styles.title}>
            {rank != null && <span className={styles.rank}>#{rank}</span>}
            <span className={styles.titleText}>{title}</span>
          </h4>
          {caption && <p className={styles.caption}>{caption}</p>}
        </div>

        {score != null && score > 0 && (
          <span className={styles.score}>
            <StarIcon className={styles.star} />
            {score.toFixed(2)}
          </span>
        )}
      </header>

      {meta.length > 0 && (
        <p className={styles.meta}>
          {meta.map((item, index) => (
            <React.Fragment key={item}>
              {index > 0 && <span className={styles.metaDot}>·</span>}
              <span>{item}</span>
            </React.Fragment>
          ))}
        </p>
      )}

      {shots.length > 0 ? (
        <div className={styles.shots}>
          {shots.map((shot) => (
            <img
              key={shot.url}
              className={styles.shot}
              src={shot.url}
              alt={shot.alt ?? ""}
              loading="lazy"
            />
          ))}
        </div>
      ) : (
        loading && (
          <div className={styles.shots}>
            {Array.from({ length: MAX_SCREENSHOTS }).map((_, index) => (
              <span key={index} className={cs(styles.shot, styles.skeleton)} />
            ))}
          </div>
        )
      )}

      {description ? (
        <p
          className={styles.description}
          style={{ WebkitLineClamp: descriptionClampLines }}
        >
          {description}
        </p>
      ) : (
        loading && (
          <div className={styles.descriptionSkeleton}>
            {Array.from({ length: descriptionClampLines }).map((_, index) => (
              <span key={index} className={cs(styles.line, styles.skeleton)} />
            ))}
          </div>
        )
      )}

      {footnote && <p className={styles.footnote}>{footnote}</p>}
    </article>
  );
};

export default AppPreviewCard;
